import { Provider } from 'react-redux';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { store } from './store/store';
import { ThemeProvider } from './contexts/ThemeContext';
import { WorkplaceCurrencyProvider } from './contexts/WorkplaceCurrencyContext';
import { AuthProvider } from './providers/AuthProvider';
import { PERMISSION_KEYS } from '@/types/roles';
import './lib/i18n';

// Layouts
import HomeRoute from "./components/routing/HomeRoute";
import AppLayout from "./components/layout/AppLayout";
import { AuthenticatedRoutesLayout } from "./components/layout/AuthenticatedRoutesLayout";

// Auth components
import ProtectedRoute from "./components/auth/ProtectedRoute";
import { ConsentGate } from "./components/auth/ConsentGate";
import ErrorBoundary from "./components/ErrorBoundary";

// Public pages
import Auth from "./pages/Auth";
import AcceptInvite from "./pages/AcceptInvite";
import Onboarding from "./pages/Onboarding";
import Pricing from "./pages/Pricing";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import DataProcessingAddendum from "./pages/DataProcessingAddendum";
import NotFound from "./pages/NotFound";
import AccessDenied from "./pages/AccessDenied";

// Dashboard
import Index from "./pages/Index";

// Employee pages
import MyTimesheets from "./pages/MyTimesheets";
import LeaveRequest from "./pages/LeaveRequest";
import MySchedule from "./pages/MySchedule";
import Payslips from "./pages/Payslips";
import NotificationsPage from "./pages/NotificationsPage";
import SettingsPage from "./pages/SettingsPage.tsx";

// Manager pages
import TeamManagement from "./pages/TeamManagement";
import ManagerTimesheets from "./pages/ManagerTimesheets";
import PayrollManagement from "./pages/PayrollManagement";
import ManagerLeave from "./pages/ManagerLeave";
import HistoryManagement from "./pages/HistoryManagement";
import SchedulePlanner from "./pages/SchedulePlanner";
import RosterPlanner from "./pages/RosterPlanner";
import AttendanceActivityPage from "./pages/AttendanceActivityPage";

// Business settings pages
import BillingSettings from "./pages/BillingSettings";
import {
  GeneralSettings,
  PeopleSettings,
  AccessPermissionsSettings,
  SchedulingSettings,
  TimeAttendanceSettings,
  CommunicationsSettings,
  LeavePoliciesSettings,
  PaySettings,
  UpcomingFeaturesSettings,
  GeolocationSettings,
  HolidaySettings,
} from "./pages/business-settings";
import ShiftCodeManager from "./pages/business-settings/ShiftCodeManager";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      retryOnMount: true,
      retryDelay: 1000,
      staleTime: 60 * 1000,
      refetchOnWindowFocus: true,
      networkMode: 'online',
    },
    mutations: {
      retry: 1,
    },
  },
});

const App = () => (
  <Provider store={store}>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
      <WorkplaceCurrencyProvider>
        <ThemeProvider>
          <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <ErrorBoundary>
          <Routes>
            {/* Public routes - no authentication required */}
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/dpa" element={<DataProcessingAddendum />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/accept-invite" element={<AcceptInvite />} />
            <Route path="/access-denied" element={<AccessDenied />} />
            
            {/* Onboarding route (protected but outside main layout, gated by consent) */}
            <Route path="/onboarding" element={<ConsentGate><Onboarding /></ConsentGate>} />
            
            {/* Home route: shows landing page for unauthenticated, dashboard for authenticated */}
            <Route path="/" element={<HomeRoute />} />
            
            {/* 
              Authenticated routes - wrapped in AuthenticatedRoutesLayout
              This layout persists across all route navigation, preventing
              realtime subscription churn when navigating between pages.
            */}
            <Route element={<AuthenticatedRoutesLayout />}>
              {/* Dashboard — inside layout to inherit all providers */}
              <Route path="/dashboard" element={<AppLayout><ErrorBoundary><Index /></ErrorBoundary></AppLayout>} />
              
              {/* Employee routes */}
              <Route path="/timesheets" element={<AppLayout><MyTimesheets /></AppLayout>} />
              <Route path="/leave" element={<AppLayout><LeaveRequest /></AppLayout>} />
              <Route path="/my-schedule" element={<AppLayout><MySchedule /></AppLayout>} />
              <Route path="/payslips" element={<AppLayout><Payslips /></AppLayout>} />
              <Route path="/notifications" element={<AppLayout><NotificationsPage /></AppLayout>} />
              <Route path="/settings" element={
                <AppLayout>
                  <ProtectedRoute>
                    <SettingsPage />
                  </ProtectedRoute>
                </AppLayout>
              } />
              
              {/* Manager+ routes - Permission-based access control */}
              <Route path="/manager/timesheets" element={
                <AppLayout>
                  <ProtectedRoute requiredPermissions={[PERMISSION_KEYS.APPROVE_TIMESHEETS]}>
                    <ManagerTimesheets />
                  </ProtectedRoute>
                </AppLayout>
              } />
              <Route path="/manager/leave" element={
                <AppLayout>
                  <ProtectedRoute requiredPermissions={[PERMISSION_KEYS.APPROVE_LEAVE_REQUESTS]}>
                    <ManagerLeave />
                  </ProtectedRoute>
                </AppLayout>
              } />
              <Route path="/manager/schedule" element={
                <AppLayout>
                  <ProtectedRoute requiredPermissions={[PERMISSION_KEYS.ADD_EDIT_SHIFTS]}>
                    <SchedulePlanner />
                  </ProtectedRoute>
                </AppLayout>
              } />
              <Route path="/manager/roster" element={
                <AppLayout>
                  <ProtectedRoute requiredPermissions={[PERMISSION_KEYS.ROSTER_MANAGER]}>
                    <RosterPlanner />
                  </ProtectedRoute>
                </AppLayout>
              } />
              <Route path="/manager/history" element={
                <AppLayout>
                  <ProtectedRoute requiredPermissions={[
                    PERMISSION_KEYS.APPROVE_LEAVE_REQUESTS,
                    PERMISSION_KEYS.APPROVE_TIMESHEETS,
                    PERMISSION_KEYS.ROSTER_MANAGER,
                    PERMISSION_KEYS.ADD_EDIT_SHIFTS,
                    PERMISSION_KEYS.SYSTEM_CONFIG_MANAGE,
                  ]}>
                    <HistoryManagement />
                  </ProtectedRoute>
                </AppLayout>
              } />
              <Route path="/manager/payroll" element={
                <AppLayout>
                  <ProtectedRoute requiredPermissions={[PERMISSION_KEYS.TIMESHEET_STAGING]}>
                    <PayrollManagement />
                  </ProtectedRoute>
                </AppLayout>
              } />
              <Route path="/manager/attendance-activity" element={
                <AppLayout>
                  <ProtectedRoute requiredPermissions={[PERMISSION_KEYS.APPROVE_TIMESHEETS]}>
                    <AttendanceActivityPage />
                  </ProtectedRoute>
                </AppLayout>
              } />
              
              {/* Team management - Permission-based access control */}
              <Route path="/team" element={
                <AppLayout>
                  <ProtectedRoute requiredPermissions={[PERMISSION_KEYS.VIEW_EMPLOYEE_DETAILS, PERMISSION_KEYS.MANAGE_EMPLOYEE_DETAILS]} requireAllPermissions={false}>
                    <TeamManagement />
                  </ProtectedRoute>
                </AppLayout>
              } />
              
              {/* Business Settings routes - Permission-based access control */}
              <Route path="/business-settings/general" element={
                <AppLayout>
                  <ProtectedRoute requiredPermissions={[PERMISSION_KEYS.SYSTEM_CONFIG_VIEW, PERMISSION_KEYS.SYSTEM_CONFIG_MANAGE]} requireAllPermissions={false}>
                    <GeneralSettings />
                  </ProtectedRoute>
                </AppLayout>
              } />
              <Route path="/business-settings/people" element={
                <AppLayout>
                  <ProtectedRoute requiredPermissions={[PERMISSION_KEYS.MANAGE_EMPLOYEE_DETAILS]}>
                    <PeopleSettings />
                  </ProtectedRoute>
                </AppLayout>
              } />
              <Route path="/business-settings/access-permissions" element={
                <AppLayout>
                  <ProtectedRoute requiredPermissions={[PERMISSION_KEYS.CAN_MANAGE_ROLES]}>
                    <AccessPermissionsSettings />
                  </ProtectedRoute>
                </AppLayout>
              } />
              <Route path="/business-settings/scheduling" element={
                <AppLayout>
                  <ProtectedRoute requiredPermissions={[PERMISSION_KEYS.SYSTEM_CONFIG_VIEW, PERMISSION_KEYS.SYSTEM_CONFIG_MANAGE]} requireAllPermissions={false}>
                    <SchedulingSettings />
                  </ProtectedRoute>
                </AppLayout>
              } />
              <Route path="/business-settings/shift-codes" element={
                <AppLayout>
                  <ProtectedRoute requiredPermissions={[PERMISSION_KEYS.SYSTEM_CONFIG_MANAGE]}>
                    <ShiftCodeManager />
                  </ProtectedRoute>
                </AppLayout>
              } />
              <Route path="/business-settings/time-attendance" element={
                <AppLayout>
                  <ProtectedRoute requiredPermissions={[PERMISSION_KEYS.SYSTEM_CONFIG_VIEW, PERMISSION_KEYS.SYSTEM_CONFIG_MANAGE]} requireAllPermissions={false}>
                    <TimeAttendanceSettings />
                  </ProtectedRoute>
                </AppLayout>
              } />
              <Route path="/business-settings/geolocation" element={
                <AppLayout>
                  <ProtectedRoute requiredPermissions={[PERMISSION_KEYS.SYSTEM_CONFIG_MANAGE]}>
                    <GeolocationSettings />
                  </ProtectedRoute>
                </AppLayout>
              } />
              <Route path="/business-settings/communications" element={
                <AppLayout>
                  <ProtectedRoute requiredPermissions={[PERMISSION_KEYS.SYSTEM_CONFIG_MANAGE]}>
                    <CommunicationsSettings />
                  </ProtectedRoute>
                </AppLayout>
              } />
              <Route path="/business-settings/leave-policies" element={
                <AppLayout>
                  <ProtectedRoute requiredPermissions={[PERMISSION_KEYS.SYSTEM_CONFIG_MANAGE]}>
                    <LeavePoliciesSettings />
                  </ProtectedRoute>
                </AppLayout>
              } />
              <Route path="/business-settings/pay" element={
                <AppLayout>
                  <ProtectedRoute requiredPermissions={[PERMISSION_KEYS.MANAGE_EMPLOYEE_PAY]}>
                    <ErrorBoundary>
                      <PaySettings />
                    </ErrorBoundary>
                  </ProtectedRoute>
                </AppLayout>
              } />
              <Route path="/business-settings/holidays" element={
                <AppLayout>
                  <ProtectedRoute requiredPermissions={[PERMISSION_KEYS.SYSTEM_CONFIG_VIEW, PERMISSION_KEYS.SYSTEM_CONFIG_MANAGE]} requireAllPermissions={false}>
                    <ErrorBoundary>
                      <HolidaySettings />
                    </ErrorBoundary>
                  </ProtectedRoute>
                </AppLayout>
              } />
              <Route path="/business-settings/billing" element={
                <AppLayout>
                  <ProtectedRoute requiredPermissions={[PERMISSION_KEYS.SYSTEM_CONFIG_MANAGE]}>
                    <ErrorBoundary>
                      <BillingSettings />
                    </ErrorBoundary>
                  </ProtectedRoute>
                </AppLayout>
              } />
              <Route path="/business-settings/upcoming-features" element={
                <AppLayout>
                  <ProtectedRoute requiredPermissions={[PERMISSION_KEYS.SYSTEM_CONFIG_VIEW, PERMISSION_KEYS.SYSTEM_CONFIG_MANAGE]} requireAllPermissions={false}>
                    <UpcomingFeaturesSettings />
                  </ProtectedRoute>
                </AppLayout>
              } />
              
              {/* Legacy billing route - redirect to new structure */}
              <Route path="/settings/billing" element={
                <AppLayout>
                  <ProtectedRoute requiredPermissions={[PERMISSION_KEYS.SYSTEM_CONFIG_MANAGE]}>
                    <ErrorBoundary>
                      <BillingSettings />
                    </ErrorBoundary>
                  </ProtectedRoute>
                </AppLayout>
              } />
            </Route>
            
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </ErrorBoundary>
        </BrowserRouter>
       </TooltipProvider>
     </ThemeProvider>
       </WorkplaceCurrencyProvider>
      </AuthProvider>
   </QueryClientProvider>
 </Provider>
);

export default App;
